import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, X, SkipForward, Volume2, Lock, ChevronUp, ChevronDown } from 'lucide-react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useNavigate } from 'react-router-dom';

/**
 * Floating mini-player rendered globally near root of <App />.
 * - Persists across route changes (perfect for shift workers / commuters).
 * - Owns the actual <audio> element.
 * - Wires the MediaSession API for lock-screen / bluetooth controls.
 * - Enforces preview cutoff for non-paying tiers.
 */

const fmtTime = (t) => {
  if (!Number.isFinite(t)) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const FloatingAudioPlayer = () => {
  const navigate = useNavigate();
  const {
    track, isPlaying, currentTime, duration,
    previewLimitSec, previewBlocked,
    setIsPlaying, setCurrentTime, setDuration, setPreviewBlocked,
    registerControls, stop, emit,
  } = useAudioPlayer();

  const audioRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);

  // Imperative controls handed back to the context
  useEffect(() => {
    registerControls({
      play: () => audioRef.current?.play().catch(() => {}),
      pause: () => audioRef.current?.pause(),
      seek: (t) => { if (audioRef.current) audioRef.current.currentTime = t; },
      stop: () => {
        const a = audioRef.current;
        if (a) {
          a.pause();
          a.removeAttribute('src');
          a.load();
        }
      },
    });
  }, [registerControls]);

  // Load + autoplay when track changes
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (!track?.src) {
      a.pause();
      return;
    }
    a.src = track.src;
    a.load();
    setPreviewBlocked(false);
    a.play().catch(() => { /* autoplay may be blocked — user can press play */ });
  }, [track?.src, setPreviewBlocked]);

  // MediaSession API — lock-screen controls
  useEffect(() => {
    if (!('mediaSession' in navigator) || !track) return;
    try {
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.topicTitle || 'Sleep Topic',
        artist: 'Dr. Edgar / Sleep Health Services',
        album: track.language === 'es' ? 'Temas de Sueño' : 'Sleep Topics',
        artwork: [
          { src: '/manifest-icon.png', sizes: '512x512', type: 'image/png' },
        ],
      });
      navigator.mediaSession.setActionHandler('play', () => audioRef.current?.play());
      navigator.mediaSession.setActionHandler('pause', () => audioRef.current?.pause());
      navigator.mediaSession.setActionHandler('seekbackward', (d) => {
        const a = audioRef.current;
        if (a) a.currentTime = Math.max(0, a.currentTime - (d.seekOffset || 10));
      });
      navigator.mediaSession.setActionHandler('seekforward', (d) => {
        const a = audioRef.current;
        if (a) a.currentTime = Math.min(a.duration || 0, a.currentTime + (d.seekOffset || 10));
      });
      navigator.mediaSession.setActionHandler('stop', stop);
    } catch { /* MediaMetadata unsupported in some browsers */ }
  }, [track, stop]);

  // ---- Audio element handlers ----
  const handlePlay = () => {
    setIsPlaying(true);
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
    emit({ type: 'play', position: audioRef.current?.currentTime || 0 });
  };
  const handlePause = () => {
    setIsPlaying(false);
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
    emit({ type: 'pause', position: audioRef.current?.currentTime || 0 });
  };
  const handleTimeUpdate = () => {
    const a = audioRef.current;
    if (!a) return;
    setCurrentTime(a.currentTime);
    // Preview cutoff enforcement
    if (previewLimitSec != null && a.currentTime >= previewLimitSec) {
      a.pause();
      a.currentTime = previewLimitSec;
      setPreviewBlocked(true);
      emit({ type: 'preview_blocked', position: previewLimitSec });
    }
    emit({ type: 'timeupdate', position: a.currentTime });
  };
  const handleLoaded = () => {
    const d = audioRef.current?.duration || 0;
    setDuration(d);
  };
  const handleEnded = () => {
    setIsPlaying(false);
    emit({ type: 'ended', position: audioRef.current?.currentTime || 0 });
  };
  const handleSeeked = () => {
    emit({ type: 'seeked', position: audioRef.current?.currentTime || 0 });
  };

  if (!track) return null;

  const effectiveDuration = duration || track.duration || 0;
  const progressPct = effectiveDuration > 0 ? (currentTime / effectiveDuration) * 100 : 0;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] pointer-events-none"
      data-testid="floating-audio-player"
    >
      <div className="container mx-auto px-2 pb-2 sm:pb-3">
        <div
          className={`pointer-events-auto bg-slate-900/95 backdrop-blur-md text-white rounded-xl shadow-2xl ring-1 ring-white/10 transition-all ${
            collapsed ? 'max-w-xs ml-auto' : 'w-full'
          }`}
        >
          {/* Top row */}
          <div className="flex items-center gap-3 px-3 sm:px-4 py-2 sm:py-3">
            {/* Cover / brand */}
            <div className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 rounded-lg bg-gradient-to-br from-blue-600 via-indigo-600 to-rose-600 flex items-center justify-center shadow-inner">
              <Volume2 className="h-5 w-5 text-white" />
            </div>

            {/* Title + meta */}
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate(`/topic/${track.topicId}`)}
                className="text-left w-full"
                data-testid="audio-track-title"
                title={track.topicTitle}
              >
                <p className="text-sm font-semibold truncate">{track.topicTitle}</p>
                <p className="text-[11px] text-white/60 truncate">
                  Dr. Edgar · {track.language === 'es' ? 'Español' : 'English'}
                  {previewLimitSec != null && (
                    <span className="ml-2 text-amber-300">· {Math.round(previewLimitSec)}s preview</span>
                  )}
                </p>
              </button>
            </div>

            {/* Play / Pause */}
            <button
              onClick={() => {
                const a = audioRef.current;
                if (!a) return;
                if (a.paused) a.play().catch(() => {});
                else a.pause();
              }}
              className="h-10 w-10 rounded-full bg-white text-slate-900 hover:scale-105 transition-transform flex items-center justify-center"
              data-testid="audio-playpause"
              aria-label={isPlaying ? 'Pause' : 'Play'}
              disabled={previewBlocked}
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
            </button>

            {/* Collapse */}
            <button
              onClick={() => setCollapsed(c => !c)}
              className="hidden sm:flex h-8 w-8 rounded-full hover:bg-white/10 items-center justify-center"
              aria-label={collapsed ? 'Expand' : 'Collapse'}
              data-testid="audio-collapse"
            >
              {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {/* Close */}
            <button
              onClick={stop}
              className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center"
              aria-label="Close audio player"
              data-testid="audio-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Progress (hidden when collapsed) */}
          {!collapsed && (
            <div className="px-3 sm:px-4 pb-3">
              <div
                className="h-1.5 bg-white/10 rounded-full overflow-hidden cursor-pointer"
                onClick={(e) => {
                  if (previewBlocked) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const ratio = (e.clientX - rect.left) / rect.width;
                  const target = ratio * effectiveDuration;
                  if (previewLimitSec != null && target > previewLimitSec) return;
                  if (audioRef.current) audioRef.current.currentTime = target;
                }}
                role="slider"
                aria-label="Seek"
                data-testid="audio-progress-bar"
              >
                <div
                  className="h-full bg-gradient-to-r from-blue-400 via-indigo-400 to-rose-400"
                  style={{ width: `${progressPct}%` }}
                />
                {previewLimitSec != null && effectiveDuration > 0 && (
                  <div
                    className="relative -mt-1.5 h-1.5"
                    style={{ marginLeft: `${(previewLimitSec / effectiveDuration) * 100}%` }}
                  >
                    <div className="absolute -top-0.5 w-0.5 h-2.5 bg-amber-400" />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-1.5 text-[10px] font-mono text-white/50">
                <span data-testid="audio-current-time">{fmtTime(currentTime)}</span>
                <span data-testid="audio-duration">{fmtTime(effectiveDuration)}</span>
              </div>

              {/* Preview-blocked CTA */}
              {previewBlocked && (
                <div
                  className="mt-2 rounded-lg bg-amber-500/15 border border-amber-400/40 px-3 py-2 flex items-center gap-2"
                  data-testid="audio-preview-cta"
                >
                  <Lock className="h-3.5 w-3.5 text-amber-300 flex-shrink-0" />
                  <p className="text-[11px] text-amber-100 flex-1">
                    {track.language === 'es'
                      ? 'Vista previa de 20s. Mejora para escuchar el episodio completo.'
                      : '20-second preview. Upgrade to hear the full episode.'}
                  </p>
                  <button
                    onClick={() => navigate('/membership')}
                    className="text-[11px] font-semibold bg-amber-400 text-slate-900 px-2.5 py-1 rounded-full hover:bg-amber-300"
                    data-testid="audio-upgrade-cta"
                  >
                    {track.language === 'es' ? 'Mejorar' : 'Upgrade'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* The actual audio element */}
          <audio
            ref={audioRef}
            preload="metadata"
            onPlay={handlePlay}
            onPause={handlePause}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoaded}
            onEnded={handleEnded}
            onSeeked={handleSeeked}
            data-testid="audio-element"
          />
        </div>
      </div>
    </div>
  );
};

export default FloatingAudioPlayer;
